// pages/BankAccount/Edit/BankAccountEditPage.tsx
import { Button } from '@/components/ui/button';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BankAccountCreationForm from '@/components/organisms/BankAccountCreationForm';
import { bankAccountService } from '@/services/bank-accounts.service';
import { BankAccountFormValues } from '@/services/types/bank-accounts.types';
import toast from 'react-hot-toast';
import InfoModal from '@/components/molecules/InfoModal';
import { ReactComponent as BankAccountUpdatedIcon } from '@/assets/icons/bank_account_created.svg';
import profileDiscardIcon from '@/assets/icons/profile-discard.svg';
import { useNavigationGuard } from '@/hooks/useNavigationGuard';
import MobileAppBar from '@/components/molecules/MobileAppBar';

const BankAccountEditPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [defaultValues, setDefaultValues] = useState<BankAccountFormValues | null>(null);
  const [maxAmount, setMaxAmount] = useState(0);
  const [isDefaultAccount, setIsDefaultAccount] = useState(true);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const { budgetId, bankAccountId } = useParams();
  const navigate = useNavigate();

  const { isDiscardModalOpen, handleCancel, handleDiscardChanges, handleDiscardModalClose, allowNavigation } =
    useNavigationGuard({
      isDirty: isFormDirty,
      fallbackPath: `/budgets/${budgetId}/bank-accounts/${bankAccountId}`
    });

  if (!budgetId || !bankAccountId) {
    console.error('Budget ID or Bank Account ID is missing from URL parameters');
    return <div>Error: Budget ID and Bank Account ID are required</div>;
  }

  // Fetch bank account data when component mounts
  useEffect(() => {
    const fetchBankAccount = async () => {
      try {
        setIsLoading(true);
        const response = await bankAccountService.getBankAccount(budgetId, bankAccountId);
        const bankAccount = response.data;

        // Transform API data to form values
        setDefaultValues({
          accountName: bankAccount.accountName,
          bankName: bankAccount.bankName,
          country: bankAccount.bankCountry,
          accountNumber: bankAccount.accountNumber,
          initialAmount: bankAccount.initialAmount.toString()
        });
        setMaxAmount(bankAccount.btev || 0);
        setIsDefaultAccount(bankAccount.is_default ?? true);
      } catch (error) {
        console.error('Error fetching bank account:', error);
        toast.error('Failed to load bank account details. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBankAccount();
  }, [budgetId, bankAccountId]);

  const handleSubmit = async (data: BankAccountFormValues) => {
    setIsSubmitting(true);
    try {
      const bankAccountData = {
        accountName: data.accountName,
        bankName: data.bankName,
        bankCountry: data.country,
        accountNumber: data.accountNumber,
        initialAmount: Number(data.initialAmount)
      };

      await bankAccountService.updateBankAccount(budgetId, bankAccountId, bankAccountData);

      allowNavigation();
      setShowSuccessModal(true);
    } catch (error) {
      console.log('Error updating bank account:', error);
      toast.error('Failed to update bank account. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleButtonClick = () => {
    // Trigger form submission
    const form = document.getElementById('bank-account-creation-form') as HTMLFormElement;
    if (form) {
      form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
  };

  const handleGoToBankAccount = () => {
    setShowSuccessModal(false);
    allowNavigation();
    // Replace so this edit page doesn't remain in history — otherwise the
    // next back press (mobile app bar or browser back) would return here.
    navigate(`/budgets/${budgetId}/bank-accounts/${bankAccountId}`, { replace: true });
  };

  if (isLoading) {
    return (
      <div className="bg-[#FBFBFF] p-4 flex flex-col items-center justify-center h-full">
        <p>Loading bank account details...</p>
      </div>
    );
  }

  return (
    <>
      <MobileAppBar title="Edit Bank Account" />
      <div className="bg-[#FBFBFF] p-4 flex flex-col flex-1 md:bg-white rounded-[16px] border">
        <h2 className="text-center font-bold mb-5 md:text-left md:text-[24px] md:mb-6">Edit Bank Account</h2>

        {/* FORM */}
        {defaultValues && (
          <BankAccountCreationForm
            onSubmit={handleSubmit}
            className="mb-6 flex-1"
            isSubmitting={isSubmitting}
            defaultValues={defaultValues}
            isEditMode={true}
            maxAmount={maxAmount}
            isDefault={isDefaultAccount}
            onFormStateChange={({ isDirty }) => setIsFormDirty(isDirty)}
          />
        )}

        {/* FOOTER */}
        <footer className="py-2 bg-gradient-to-b from-[#F0EFFD]/0 from-0% to-[#F0EFFD] to-[17.74%] md:bg-white md:bg-none md:from-transparent md:to-transparent md:flex md:justify-end">
          <Button
            className="hidden md:block rounded-[24px] text-base font-semibold px-6 h-[44px] text-[16px] w-full md:w-fit text-[#E8507E]"
            variant={'text'}
            onClick={() => handleCancel()}
            disabled={isSubmitting}
            type="button"
          >
            Cancel
          </Button>
          <Button
            className="rounded-[24px] text-base font-semibold px-6 h-[44px] text-[16px] w-full md:w-fit"
            onClick={handleButtonClick}
            disabled={isSubmitting || isLoading}
            type="button"
          >
            {isSubmitting ? 'SAVING...' : 'SAVE'}
          </Button>
        </footer>
      </div>

      {isDiscardModalOpen && (
        <InfoModal
          isOpen={isDiscardModalOpen}
          variant="confirm"
          title="Are you sure you want to discard changes?"
          message="Unsaved changes will not be saved into your bank account"
          buttonText="Discard"
          onButtonClick={handleDiscardChanges}
          icon={profileDiscardIcon}
          onClose={handleDiscardModalClose}
        />
      )}

      {showSuccessModal && (
        <InfoModal
          isOpen={showSuccessModal}
          variant="success"
          title="Bank Account Updated"
          message={<p>You have successfully updated the Bank Account!</p>}
          buttonText="Go to Bank Account"
          onButtonClick={handleGoToBankAccount}
          icon={<BankAccountUpdatedIcon />}
        />
      )}
    </>
  );
};

export default BankAccountEditPage;
